import { Group, Loader, Modal, MultiSelect, Stack, TextInput } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { useEffect } from 'react';
import type { ProjectsResponse } from '../../../../api/projects-client/projects-client.type';
import { Button } from '../../../../components/button/button';
import { Text } from '../../../../ui-library/typography/typography';
import { toast } from '../../../notifications';
import { useUpdateProject } from './project.hooks';

type EditProjectFormValues = {
  name: string;
  subprojectIds: string[];
};

function EditProjectWizard({
  project,
  allProjects,
  opened,
  onClose,
  onSubmit,
}: {
  project: ProjectsResponse | null;
  allProjects: ProjectsResponse[];
  opened: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const { updateProjectMutation, isUpdating } = useUpdateProject({
    onSuccess: onSubmit,
    onError: (error) => {
      toast.error(`Failed to update project: ${error.message}`);
    },
  });

  const form = useForm<EditProjectFormValues>({
    validateInputOnChange: true,
    initialValues: {
      name: project?.name || '',
      subprojectIds: project?.subprojects?.map((s) => s.id) || [],
    },
    validate: {
      name: isNotEmpty(),
      subprojectIds: isNotEmpty(),
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding form.setValues causes infinite render loop
  useEffect(() => {
    if (project && opened) {
      form.setValues({
        name: project.name,
        subprojectIds: project.subprojects?.map((s) => s.id) || [],
      });
    }
  }, [project, opened]);

  const subprojectOptions = allProjects
    .filter((p) => p.id !== project?.id)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((p) => ({
      group: p.name,
      items: p.subprojects
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((subproject) => ({ value: subproject.id, label: subproject.name })),
    }));

  const currentProjectBoards =
    project?.subprojects?.map((s) => ({
      value: s.id,
      label: s.name,
    })) || [];

  const allOptions = [{ group: project?.name || 'Current Project', items: currentProjectBoards }, ...subprojectOptions];

  function handleClose() {
    onClose();
    form.reset();
  }

  function handleSubmit() {
    if (!project || !form.values.name || !form.values.subprojectIds.length) {
      return;
    }
    updateProjectMutation({
      id: project.id,
      name: form.values.name,
      subproject_ids: form.values.subprojectIds,
    });
  }

  return (
    <Modal title="Edit Project" opened={opened} onClose={handleClose}>
      <Stack gap="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput label="Project Name" {...form.getInputProps('name')} radius="xl" />
            <MultiSelect
              label="Boards"
              disabled={allOptions.length === 0}
              placeholder="Select Boards"
              {...form.getInputProps('subprojectIds')}
              data={allOptions}
              searchable
              clearable
              comboboxProps={{ withinPortal: true, zIndex: 1000 }}
              radius="xl"
            />
            <Text size="small" color="gray">
              Note: Only synthetic projects can be edited.
            </Text>
            <Group justify="center" gap="md">
              <Button variant="outline" radius="xl" onClick={handleClose}>
                Cancel
              </Button>
              <Button disabled={!form.isValid() || isUpdating} type="submit" radius="xl">
                {isUpdating ? <Loader size="sm" /> : 'Save'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
}

export { EditProjectWizard };
